import {RealtimeSessionUpdate, VoiceConversationItem} from 'llm/realtime-voice/OpenAIRealtimeEvents';
import * as Net from 'util/Net';

interface StartWebRTCRealtimeVoiceSessionResponse {
  sdpAnswer: string;
  /** Prior turns to replay into the OpenAI Realtime session. */
  conversationHistory?: Array<VoiceConversationItem>;
}

export interface RealtimeVoiceConsentStatus {
  hasCurrentAcceptance: boolean;
  /** False when the authenticated session is exempt from accepting Voice terms. */
  isAcceptanceRequired?: boolean;
  requiredVersionDate: string;
}

/** Return the authenticated user's server-owned status for the current Voice terms. */
export async function getRealtimeVoiceConsentStatus(): Promise<RealtimeVoiceConsentStatus> {
  return await Net.getJSONPromise('/1/api/llm/data-capture-assistant/realtime-voice-consent');
}

/** Persist acceptance of the server-selected current Voice terms. */
export async function acceptRealtimeVoiceTerms(): Promise<RealtimeVoiceConsentStatus> {
  return await Net.postJSONPromise({
    url: '/1/api/llm/data-capture-assistant/realtime-voice-consent',
    data: {},
  });
}

/**
 * For a given Compose chat session, start a realtime session with the OpenAI API.
 */
export async function startWebRTCRealtimeVoiceSession(
  chatSessionId: string,
  sdpOffer: string,
  activeWorkspaceContextId?: string
): Promise<StartWebRTCRealtimeVoiceSessionResponse> {
  return await Net.postJSONPromise({
    url: `/1/api/llm/data-capture-assistant/chats/${chatSessionId}:start-webrtc-realtime-voice-session`,
    data: {
      sdpOffer,
      ...(activeWorkspaceContextId == null ? {} : {activeWorkspaceContextId}),
    },
  });
}

export interface VoiceToolInvocationItem {
  sessionId?: string;
  itemId?: string;
  previousItemId?: string;
  name: string;
  callId: string;
  args: string;
}

export interface VoiceToolInvocationResponse {
  output: string;
  sessionUpdate?: RealtimeSessionUpdate;
  draftDocumentCreated: boolean;
  /** Server-authorized Benchling object references for local chat display only. */
  displayReferenceIds?: Array<string>;
}

/**
 * In the context of a realtime voice session, execute a tool invoked by the LLM.
 * Note that the tool name and args have not been validated at this point and must be
 * validated by the server.
 */
export async function invokeVoiceTool(
  chatSessionId: string,
  item: VoiceToolInvocationItem,
  activeWorkspaceContextId?: string,
  userTimezone?: string
): Promise<VoiceToolInvocationResponse> {
  return await Net.postJSONPromise({
    url: `/1/api/llm/data-capture-assistant/chats/${chatSessionId}:invoke-voice-tool`,
    data: {
      item,
      ...(activeWorkspaceContextId == null ? {} : {activeWorkspaceContextId}),
      ...(userTimezone == null ? {} : {userTimezone}),
    },
  });
}

export interface PersistVoiceTranscriptRequest {
  role: 'user' | 'assistant';
  text: string;
  itemId: string;
  previousItemId?: string | null;
  failed?: boolean;
  openaiRealtimeSessionId?: string;
}

export interface PersistVoiceTranscriptResponse {
  eventId: string;
}

/** Persist a transcript through the server-owned Voice provenance endpoint. */
export async function persistVoiceTranscript(
  chatSessionId: string,
  request: PersistVoiceTranscriptRequest
): Promise<PersistVoiceTranscriptResponse> {
  return await Net.postJSONPromise({
    url: `/1/api/llm/data-capture-assistant/chats/${chatSessionId}:persist-voice-transcript`,
    data: request,
  });
}
